/*
INSTRUCCIONES DE CONFIGURACIÓN DE SUPABASE:

1. Instalar la dependencia de Supabase:
   npm install @supabase/supabase-js

2. Configurar las variables de entorno en tu proyecto:
   - Reemplaza 'YOUR_SUPABASE_URL' con tu URL de Supabase
   - Reemplaza 'YOUR_SUPABASE_ANON_KEY' con tu clave anónima de Supabase

3. Estructura de la tabla 'predicciones' en Supabase:
   - nomenclatura (text)
   - entidad (text) 
   - descripcion (text)
   - prediccion (text)
   - valor_real (text) - "PENDIENTE" para predicciones en vivo, cualquier otro valor para verificadas
   - buena_pro (text)
   - tipo (text)
   - created_at (timestamp) - se usa para ordenar los resultados

4. Configurar las políticas de seguridad (RLS) en Supabase para permitir lectura pública de la tabla predicciones.

NOTA: Si valor_real = "PENDIENTE" → se muestra en "Predicciones En Vivo"
      Si valor_real ≠ "PENDIENTE" → se muestra en "Predicciones Verificadas"
*/

import React, { useState, useEffect } from 'react';
import { createClient } from '@supabase/supabase-js';
import { 
  Brain, Shield, Phone, CheckCircle, Award, 
  BarChart3, ArrowRight, FileText, Sparkles,
  TrendingUp, Target, Zap, Star, Users, Rocket,
  Clock, DollarSign, Crown, Gem, Activity
} from 'lucide-react';

// Configuración de Supabase
const supabaseUrl = 'https://shodhqawnbqcqyygfkmo.supabase.co';
const supabaseKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNob2RocWF3bmJxY3F5eWdma21vIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQyMzA1MzgsImV4cCI6MjA2OTgwNjUzOH0.Er1WNXOEDgCwBtd69iN9Pe_Nj0zWXgQmBIGZ1VCjE80';
const supabase = createClient(supabaseUrl, supabaseKey);

const PrediccionLicitaciones = () => {
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [activeFeature, setActiveFeature] = useState(0);
  const [prediccionesEnVivo, setPrediccionesEnVivo] = useState([]);
  const [prediccionesVerificadas, setPrediccionesVerificadas] = useState([]);
  const [loading, setLoading] = useState(true);

  const handleContact = () => {
    window.open('https://wa.me/51942128139?text=Hola,%20quiero%20solicitar%20una%20predicción%20del%20valor%20de%20cuantía%20para%20mi%20licitación%20pública', '_blank');
  };

// Función para cargar datos de Supabase
  const cargarPredicciones = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('predicciones')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error cargando predicciones:', error);
        setPrediccionesEnVivo([]);
        setPrediccionesVerificadas([]);
        return;
      }

      console.log('Datos recibidos de Supabase:', data);

      // Separar predicciones en vivo vs verificadas basado en valor_real
      const enVivo = data ? data.filter(item => item.valor_real === 'PENDIENTE') : [];
      const verificadas = data ? data.filter(item => item.valor_real !== 'PENDIENTE') : [];

      console.log('Predicciones en vivo:', enVivo);
      console.log('Predicciones verificadas:', verificadas);

      setPrediccionesEnVivo(enVivo);
      setPrediccionesVerificadas(verificadas);
      
    } catch (error) {
      console.error('Error conectando con Supabase:', error);
      setPrediccionesEnVivo([]);
      setPrediccionesVerificadas([]);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    cargarPredicciones();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 3);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: <Brain size={38} />,
      title: "IA Especializada",
      description: "Modelo predictivo entrenado con más de 50,000 licitaciones reales del Estado Peruano para máxima precisión",
      stat: "90%",
      label: "Precisión Garantizada",
      gradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
      color: "#6366f1",
      lightBg: "#eef2ff"
    },
    {
      icon: <Target size={38} />,
      title: "Rango Predictivo",
      description: "Límites inferior y superior calculados con intervalo de confianza estadístico del 95% basado en datos históricos",
      stat: "95%",
      label: "Nivel de Confianza",
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      color: "#10b981",
      lightBg: "#ecfdf5"
    },
    {
      icon: <Shield size={38} />,
      title: "Garantía Total",
      description: "Devolución automática del 70% del costo si el valor final cae fuera del rango predicho por nuestro sistema",
      stat: "70%",
      label: "Garantía",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      color: "#f59e0b",
      lightBg: "#fffbeb"
    }
  ];

  const benefits = [  ];

  const testimonials = [
    {
      text: "Esta predicción cambió completamente nuestra estrategia comercial. Ganamos la licitación más importante del año gracias a los datos precisos que nos proporcionaron. El nivel de detalle del análisis es verdaderamente impresionante.",
      author: "Carlos Mendoza",
      role: "Gerente Comercial",
      company: "Constructora del Sur SAC",
      avatar: "C",
      project: "Licitación S/ 2.5M adjudicada",
      color: "#6366f1"
    },
    {
      text: "La precisión del rango predicho fue asombrosa. Ahora sabemos exactamente qué licitaciones vale la pena perseguir y cuáles no. Nos han ahorrado cientos de horas de análisis y miles de soles en recursos dedicados a evaluación.",
      author: "María Fernández",
      role: "CEO & Fundadora",
      company: "Consultora Apex Perú",
      avatar: "M",
      project: "15 licitaciones evaluadas exitosamente",
      color: "#10b981"
    },
    {
      text: "Profesionalismo del más alto nivel. El reporte fue tan detallado y bien estructurado que nos dio total confianza para tomar decisiones estratégicas de largo plazo. Totalmente recomendado para cualquier empresa seria del sector.",
      author: "Roberto Lima",
      role: "Director de Licitaciones",
      company: "Consorcio Nacional",
      avatar: "R",
      project: "Estrategia anual completa implementada",
      color: "#f59e0b"
    }
  ];

  const processSteps = [
    { 
      number: "1", 
      title: "Contacto Express", 
      desc: "Escríbenos por WhatsApp con tu código SEACE o los detalles básicos de tu licitación",
      icon: <Phone size={30} />,
      time: "2 minutos",
      gradient: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
      color: "#6366f1"
    },
    { 
      number: "2", 
      title: "Revisión Documentación", 
      desc: "Revisamos la información del proceso y lo agregamos a nuestro modelo predictivo",
      icon: <FileText size={30} />,
      time: "5 minutos",
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
      color: "#10b981"
    },
    { 
      number: "3", 
      title: "Análisis IA", 
      desc: "Nuestra inteligencia artificial procesa y analiza cada detalle de tu licitación",
      icon: <Brain size={30} />,
      time: "12-24 horas",
      gradient: "linear-gradient(135deg, #f59e0b 0%, #d97706 100%)",
      color: "#f59e0b"
    },
    { 
      number: "4", 
      title: "Predicción", 
      desc: "Recibes tu predicción completa del valor de la cuantía",
      icon: <Award size={30} />,
      time: "Inmediato",
      gradient: "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
      color: "#8b5cf6"
    }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #fafbfd 0%, #f0f4ff 25%, #ecfdf5 50%, #fffbeb 75%, #fafbfd 100%)',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      position: 'relative'
    }}>
      {/* Subtle Background Elements */}
      <div style={{
        position: 'fixed',
        top: '-15%',
        right: '-10%',
        width: '500px',
        height: '500px',
        background: 'radial-gradient(circle, rgba(99, 102, 241, 0.08), transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none'
      }} />
      <div style={{
        position: 'fixed',
        bottom: '-15%',
        left: '-10%',
        width: '600px',
        height: '600px',
        background: 'radial-gradient(circle, rgba(16, 185, 129, 0.06), transparent 70%)',
        borderRadius: '50%',
        filter: 'blur(80px)',
        pointerEvents: 'none'
      }} />

      <div style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '2rem 1rem',
        position: 'relative',
        zIndex: 1
      }}>
        
        {/* Hero Section */}
        <div style={{
          textAlign: 'center',
          padding: '3rem 0 2rem',
          marginBottom: '4rem'
        }}>
          {/* Premium Badge */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.75rem',
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1), rgba(139, 92, 246, 0.1))',
            border: '2px solid rgba(99, 102, 241, 0.25)',
            borderRadius: '1.5rem',
            padding: '0.75rem 1.5rem',
            marginBottom: '2rem',
            boxShadow: '0 6px 20px rgba(99, 102, 241, 0.12)'
          }}>
            <div style={{
              width: '36px',
              height: '36px',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 3px 10px rgba(99, 102, 241, 0.25)'
            }}>
              <Crown size={18} color="white" strokeWidth={2.5} />
            </div>
            <span style={{
              fontSize: '0.8rem',
              fontWeight: '700',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              letterSpacing: '0.05em',
              textTransform: 'uppercase'
            }}>
              🚀 Tecnología IA Premium • 90% Precisión Certificada
            </span>
          </div>

          {/* Main Headline */}
          <h1 style={{
            fontSize: 'clamp(2.25rem, 5vw, 4.5rem)',
            fontWeight: '800',
            marginBottom: '1.25rem',
            lineHeight: '1.1',
            letterSpacing: '-0.025em',
            color: '#0f172a'
          }}>
            Conoce el Valor de la Cuantía
            <br />
            <span style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #10b981 50%, #f59e0b 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'inline-block'
            }}>
              de tu Licitación
            </span>
            <br />
            <span style={{fontSize: 'clamp(1.25rem, 2.5vw, 2.25rem)', fontWeight: '600', color: '#334155', marginTop: '0.5rem', display: 'block'}}>
              Que no te descalifiquen por cotizar muy alto
            </span>
          </h1>

          <p style={{
            fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
            color: '#64748b',
            maxWidth: '700px',
            margin: '0 auto 2.5rem',
            lineHeight: '1.6',
            fontWeight: '400'
          }}>
            Obtén el{' '}
            <strong style={{
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: '700'
            }}>
              rango exacto de la cuantía
            </strong>
            {' '}del proceso que estás participando en{' '}
            <strong style={{
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              fontWeight: '700'
            }}>
             1-2 horas
            </strong>
          </p>

          {/* Stats Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '1.5rem',
            maxWidth: '1000px',
            margin: '0 auto 2.5rem'
          }}>
            {features.map((feature, index) => (
              <div key={index} style={{
                background: 'white',
                border: activeFeature === index ? `2px solid ${feature.color}` : '2px solid #e2e8f0',
                borderRadius: '1.25rem',
                padding: '2rem 1.5rem',
                boxShadow: activeFeature === index 
                  ? `0 15px 40px ${feature.color}20` 
                  : '0 3px 12px rgba(15, 23, 42, 0.05)',
                transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                transform: activeFeature === index ? 'translateY(-4px)' : 'translateY(0)',
                cursor: 'pointer',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.borderColor = feature.color;
                e.currentTarget.style.boxShadow = `0 15px 40px ${feature.color}20`;
              }}
              onMouseLeave={(e) => {
                if (activeFeature !== index) {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.borderColor = '#e2e8f0';
                  e.currentTarget.style.boxShadow = '0 3px 12px rgba(15, 23, 42, 0.05)';
                }
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-40%',
                  right: '-25%',
                  width: '150px',
                  height: '150px',
                  background: `radial-gradient(circle, ${feature.color}12, transparent 70%)`,
                  borderRadius: '50%',
                  pointerEvents: 'none'
                }} />
                
                <div style={{
                  display: 'inline-flex',
                  padding: '0.75rem',
                  background: feature.gradient,
                  borderRadius: '0.875rem',
                  marginBottom: '1.25rem',
                  boxShadow: `0 6px 16px ${feature.color}30`,
                  color: 'white'
                }}>
                  {feature.icon}
                </div>
                
                <div style={{
                  fontSize: '2.75rem',
                  fontWeight: '800',
                  background: feature.gradient,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '0.5rem',
                  letterSpacing: '-0.02em'
                }}>
                  {feature.stat}
                </div>
                
                <div style={{
                  fontSize: '0.7rem',
                  color: feature.color,
                  fontWeight: '700',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  marginBottom: '1rem'
                }}>
                  {feature.label}
                </div>
                
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  color: '#0f172a',
                  marginBottom: '0.75rem',
                  lineHeight: '1.3'
                }}>
                  {feature.title}
                </h3>
                
                <p style={{
                  color: '#64748b',
                  lineHeight: '1.5',
                  fontSize: '0.875rem'
                }}>
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Main CTA */}
          <button 
            onClick={handleContact}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              color: 'white',
              fontWeight: '700',
              fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
              padding: 'clamp(1rem, 2.5vw, 1.25rem) clamp(1.5rem, 4vw, 2.5rem)',
              border: 'none',
              borderRadius: '0.75rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 10px 30px rgba(99, 102, 241, 0.3)',
              marginBottom: '2rem'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 35px rgba(99, 102, 241, 0.35)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(99, 102, 241, 0.3)';
            }}
          >
            <Rocket size={20} strokeWidth={2.5} />
            Solicitar Predicción Ahora
            <ArrowRight size={20} strokeWidth={2.5} />
          </button>

          {/* Social Proof */}
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 'clamp(2rem, 5vw, 4rem)',
            flexWrap: 'wrap',
            padding: '1.5rem',
            background: 'white',
            borderRadius: '1rem',
            border: '2px solid #e2e8f0',
            boxShadow: '0 6px 20px rgba(15, 23, 42, 0.06)',
            maxWidth: '700px',
            margin: '0 auto'
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.625rem'}}>
              <Users size={22} style={{color: '#6366f1'}} strokeWidth={2.5} />
              <div>
                <div style={{fontSize: '1.5rem', fontWeight: '800', background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>200+</div>
                <div style={{fontSize: '0.7rem', color: '#64748b', fontWeight: '600'}}>Empresas</div>
              </div>
            </div>
            
            <div style={{width: '1px', height: '35px', background: '#e2e8f0'}} />
            
            <div style={{display: 'flex', alignItems: 'center', gap: '0.625rem'}}>
              <div style={{display: 'flex'}}>
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={14} fill="#f59e0b" color="#f59e0b" strokeWidth={0} />
                ))}
              </div>
              <div>
                <div style={{fontSize: '1.5rem', fontWeight: '800', color: '#f59e0b'}}>4.9</div>
                <div style={{fontSize: '0.7rem', color: '#64748b', fontWeight: '600'}}>Rating</div>
              </div>
            </div>
            
            <div style={{width: '1px', height: '35px', background: '#e2e8f0'}} />
            
            <div style={{display: 'flex', alignItems: 'center', gap: '0.625rem'}}>
              <Award size={22} style={{color: '#10b981'}} strokeWidth={2.5} />
              <div>
                <div style={{fontSize: '1.5rem', fontWeight: '800', background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent'}}>90%</div>
                <div style={{fontSize: '0.7rem', color: '#64748b', fontWeight: '600'}}>Precisión</div>
              </div>
            </div>
          </div>
        </div>

        {/* Live Predictions Table */}
        <div style={{
          background: 'white',
          border: '2px solid #e2e8f0',
          borderRadius: '1.5rem',
          padding: 'clamp(1.5rem, 4vw, 2.5rem)',
          marginBottom: '4rem',
          boxShadow: '0 6px 25px rgba(15, 23, 42, 0.06)'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '2.5rem'
          }}>
            <div style={{
              display: 'inline-flex',
              padding: '0.875rem',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
              borderRadius: '1rem',
              boxShadow: '0 6px 20px rgba(99, 102, 241, 0.25)',
              marginBottom: '1.25rem'
            }}>
              <Activity size={28} style={{color: 'white'}} strokeWidth={2} />
            </div>
            
            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: '800',
              color: '#0f172a',
              marginBottom: '0.75rem',
              letterSpacing: '-0.02em'
            }}>
              Predicciones {' '}
              <span style={{
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                En Vivo
              </span>
            </h2>
            
            <p style={{
              fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
              color: '#64748b',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
             Procesos siendo analizados por nuestra IA en tiempo real
            </p>
          </div>

          {/* Live Predictions Table Container */}
          <div style={{
            overflowX: 'auto',
            background: '#fafbfd',
            borderRadius: '1rem',
            border: '2px solid #e2e8f0',
            boxShadow: '0 3px 12px rgba(15, 23, 42, 0.04)',
            marginBottom: '2rem'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.8rem'
            }}>
              <thead>
                <tr style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                }}>
                  <th style={{
                    padding: '1rem 0.875rem',
                    textAlign: 'left',
                    fontWeight: '700',
                    color: 'white',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderRight: '1px solid rgba(255,255,255,0.2)',
                    minWidth: '160px'
                  }}>
                    Nomenclatura
                  </th>
                  <th style={{
                    padding: '1rem 0.875rem',
                    textAlign: 'left',
                    fontWeight: '700',
                    color: 'white',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderRight: '1px solid rgba(255,255,255,0.2)',
                    minWidth: '100px'
                  }}>
                    Entidad
                  </th>
                  <th style={{
                    padding: '1rem 0.875rem',
                    textAlign: 'left',
                    fontWeight: '700',
                    color: 'white',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderRight: '1px solid rgba(255,255,255,0.2)',
                    minWidth: '200px'
                  }}>
                    Descripción
                  </th>
                  <th style={{
                    padding: '1rem 0.875rem',
                    textAlign: 'center',
                    fontWeight: '700',
                    color: 'white',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderRight: '1px solid rgba(255,255,255,0.2)',
                    minWidth: '80px'
                  }}>
                    Tipo
                  </th>
                  <th style={{
                    padding: '1rem 0.875rem',
                    textAlign: 'center',
                    fontWeight: '700',
                    color: 'white',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderRight: '1px solid rgba(255,255,255,0.2)',
                    minWidth: '100px'
                  }}>
                    Buena Pro
                  </th>
                  <th style={{
                    padding: '1rem 0.875rem',
                    textAlign: 'center',
                    fontWeight: '700',
                    color: 'white',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    minWidth: '120px'
                  }}>
                    Predicción
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="6" style={{
                      padding: '2rem',
                      textAlign: 'center',
                      color: '#64748b',
                      fontSize: '0.875rem'
                    }}>
                      Cargando predicciones en vivo...
                    </td>
                  </tr>
                ) : prediccionesEnVivo.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{
                      padding: '2rem',
                      textAlign: 'center',
                      color: '#64748b',
                      fontSize: '0.875rem'
                    }}>
                      No hay predicciones en vivo en este momento
                    </td>
                  </tr>
                ) : (
                  prediccionesEnVivo.map((prediccion, index) => (
                    <tr key={index} style={{
                      background: 'white',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'white';
                    }}>
                      <td style={{
                        padding: '1.25rem 0.875rem',
                        borderBottom: '1px solid #e2e8f0',
                        borderRight: '1px solid #e2e8f0'
                      }}>
                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          color: '#6366f1',
                          marginBottom: '0.25rem'
                        }}>
                          {prediccion.nomenclatura || 'N/A'}
                        </div>
                      </td>
                      <td style={{
                        padding: '1.25rem 0.875rem',
                        borderBottom: '1px solid #e2e8f0',
                        borderRight: '1px solid #e2e8f0'
                      }}>
                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#0f172a'
                        }}>
                          {prediccion.entidad || 'N/A'}
                        </div>
                      </td>
                      <td style={{
                        padding: '1.25rem 0.875rem',
                        borderBottom: '1px solid #e2e8f0',
                        borderRight: '1px solid #e2e8f0'
                      }}>
                        <div style={{
                          fontSize: '0.75rem',
                          color: '#0f172a',
                          lineHeight: '1.4',
                          fontWeight: '500'
                        }}>
                          {prediccion.descripcion || 'N/A'}
                        </div>
                      </td>
                      <td style={{
                        padding: '1.25rem 0.875rem',
                        borderBottom: '1px solid #e2e8f0',
                        borderRight: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          color: '#64748b'
                        }}>
                          {prediccion.tipo || 'N/A'}
                        </div>
                      </td>
                      <td style={{
                        padding: '1.25rem 0.875rem',
                        borderBottom: '1px solid #e2e8f0',
                        borderRight: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#0f172a'
                        }}>
                          {prediccion.buena_pro || 'N/A'}
                        </div>
                      </td>
                      <td style={{
                        padding: '1.25rem 0.875rem',
                        borderBottom: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.375rem',
                          padding: '0.375rem 0.75rem',
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          borderRadius: '0.5rem',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          boxShadow: '0 2px 6px rgba(245, 158, 11, 0.2)'
                        }}>
                          <div style={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            background: 'white',
                            animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                          }}></div>
                          {prediccion.prediccion || 'Procesando...'}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Live Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '1rem',
            maxWidth: '500px',
            margin: '0 auto'
          }}>
            <div style={{
              textAlign: 'center',
              padding: '1rem',
              background: '#eef2ff',
              borderRadius: '0.75rem',
              border: '2px solid #c7d2fe'
            }}>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '800',
                color: '#6366f1',
                marginBottom: '0.25rem'
              }}>
                {prediccionesEnVivo.length}
              </div>
              <div style={{
                fontSize: '0.7rem',
                fontWeight: '600',
                color: '#3730a3'
              }}>
                En Proceso
              </div>
            </div>
            
            <div style={{
              textAlign: 'center',
              padding: '1rem',
              background: '#fffbeb',
              borderRadius: '0.75rem',
              border: '2px solid #fde68a'
            }}>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '800',
                color: '#f59e0b',
                marginBottom: '0.25rem'
              }}>
                1-2h
              </div>
              <div style={{
                fontSize: '0.7rem',
                fontWeight: '600',
                color: '#92400e'
              }}>
                Tiempo Promedio
              </div>
            </div>
            
            <div style={{
              textAlign: 'center',
              padding: '1rem',
              background: '#ecfdf5',
              borderRadius: '0.75rem',
              border: '2px solid #a7f3d0'
            }}>
              <div style={{
                fontSize: '1.5rem',
                fontWeight: '800',
                color: '#10b981',
                marginBottom: '0.25rem'
              }}>
                24/7
              </div>
              <div style={{
                fontSize: '0.7rem',
                fontWeight: '600',
                color: '#065f46'
              }}>
                Sistema Activo
              </div>
            </div>
          </div>
        </div>

        {/* Success Cases Table */}
        <div style={{
          background: 'white',
          border: '2px solid #e2e8f0',
          borderRadius: '1.5rem',
          padding: 'clamp(1.5rem, 4vw, 2.5rem)',
          marginBottom: '4rem',
          boxShadow: '0 6px 25px rgba(15, 23, 42, 0.06)'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '2.5rem'
          }}>
            <div style={{
              display: 'inline-flex',
              padding: '0.875rem',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              borderRadius: '1rem',
              boxShadow: '0 6px 20px rgba(16, 185, 129, 0.25)',
              marginBottom: '1.25rem'
            }}>
              <BarChart3 size={28} style={{color: 'white'}} strokeWidth={2} />
            </div>
            
            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: '800',
              color: '#0f172a',
              marginBottom: '0.75rem',
              letterSpacing: '-0.02em'
            }}>
              Predicciones {' '}
              <span style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Verificadas
              </span>
            </h2>
            
            <p style={{
              fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
              color: '#64748b',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
             Comprueba tú mismo la precisión de nuestras predicciones completadas
            </p>
          </div>

          {/* Responsive Table Container */}
          <div style={{
            overflowX: 'auto',
            background: '#fafbfd',
            borderRadius: '1rem',
            border: '2px solid #e2e8f0',
            boxShadow: '0 3px 12px rgba(15, 23, 42, 0.04)'
          }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.8rem'
            }}>
              <thead>
                <tr style={{
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                }}>
                  <th style={{
                    padding: '1rem 0.875rem',
                    textAlign: 'left',
                    fontWeight: '700',
                    color: 'white',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderRight: '1px solid rgba(255,255,255,0.2)',
                    minWidth: '160px'
                  }}>
                    Nomenclatura
                  </th>
                  <th style={{
                    padding: '1rem 0.875rem',
                    textAlign: 'left',
                    fontWeight: '700',
                    color: 'white',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderRight: '1px solid rgba(255,255,255,0.2)',
                    minWidth: '100px'
                  }}>
                    Entidad
                  </th>
                  <th style={{
                    padding: '1rem 0.875rem',
                    textAlign: 'left',
                    fontWeight: '700',
                    color: 'white',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderRight: '1px solid rgba(255,255,255,0.2)',
                    minWidth: '200px'
                  }}>
                    Descripción
                  </th>
                  <th style={{
                    padding: '1rem 0.875rem',
                    textAlign: 'center',
                    fontWeight: '700',
                    color: 'white',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderRight: '1px solid rgba(255,255,255,0.2)',
                    minWidth: '80px'
                  }}>
                    Tipo
                  </th>
                  <th style={{
                    padding: '1rem 0.875rem',
                    textAlign: 'center',
                    fontWeight: '700',
                    color: 'white',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderRight: '1px solid rgba(255,255,255,0.2)',
                    minWidth: '100px'
                  }}>
                    Buena Pro
                  </th>
                  <th style={{
                    padding: '1rem 0.875rem',
                    textAlign: 'center',
                    fontWeight: '700',
                    color: 'white',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderRight: '1px solid rgba(255,255,255,0.2)',
                    minWidth: '120px'
                  }}>
                    Predicción
                  </th>
                  <th style={{
                    padding: '1rem 0.875rem',
                    textAlign: 'center',
                    fontWeight: '700',
                    color: 'white',
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    minWidth: '110px'
                  }}>
                    Valor Real
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" style={{
                      padding: '2rem',
                      textAlign: 'center',
                      color: '#64748b',
                      fontSize: '0.875rem'
                    }}>
                      Cargando predicciones verificadas...
                    </td>
                  </tr>
                ) : prediccionesVerificadas.length === 0 ? (
                  <tr>
                    <td colSpan="7" style={{
                      padding: '2rem',
                      textAlign: 'center',
                      color: '#64748b',
                      fontSize: '0.875rem'
                    }}>
                      No hay predicciones verificadas disponibles
                    </td>
                  </tr>
                ) : (
                  prediccionesVerificadas.map((prediccion, index) => (
                    <tr key={index} style={{
                      background: 'white',
                      transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = '#f8fafc';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'white';
                    }}>
                      <td style={{
                        padding: '1.25rem 0.875rem',
                        borderBottom: '1px solid #e2e8f0',
                        borderRight: '1px solid #e2e8f0'
                      }}>
                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          color: '#6366f1',
                          marginBottom: '0.25rem'
                        }}>
                          {prediccion.nomenclatura || 'N/A'}
                        </div>
                      </td>
                      <td style={{
                        padding: '1.25rem 0.875rem',
                        borderBottom: '1px solid #e2e8f0',
                        borderRight: '1px solid #e2e8f0'
                      }}>
                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#0f172a'
                        }}>
                          {prediccion.entidad || 'N/A'}
                        </div>
                      </td>
                      <td style={{
                        padding: '1.25rem 0.875rem',
                        borderBottom: '1px solid #e2e8f0',
                        borderRight: '1px solid #e2e8f0'
                      }}>
                        <div style={{
                          fontSize: '0.75rem',
                          color: '#0f172a',
                          lineHeight: '1.4',
                          fontWeight: '500'
                        }}>
                          {prediccion.descripcion || 'N/A'}
                        </div>
                      </td>
                      <td style={{
                        padding: '1.25rem 0.875rem',
                        borderBottom: '1px solid #e2e8f0',
                        borderRight: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          fontSize: '0.7rem',
                          fontWeight: '600',
                          color: '#64748b'
                        }}>
                          {prediccion.tipo || 'N/A'}
                        </div>
                      </td>
                      <td style={{
                        padding: '1.25rem 0.875rem',
                        borderBottom: '1px solid #e2e8f0',
                        borderRight: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          fontSize: '0.75rem',
                          fontWeight: '600',
                          color: '#0f172a'
                        }}>
                          {prediccion.buena_pro || 'N/A'}
                        </div>
                      </td>
                      <td style={{
                        padding: '1.25rem 0.875rem',
                        borderBottom: '1px solid #e2e8f0',
                        borderRight: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          display: 'inline-block',
                          padding: '0.375rem 0.75rem',
                          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                          borderRadius: '0.5rem',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          boxShadow: '0 2px 6px rgba(245, 158, 11, 0.2)'
                        }}>
                          {prediccion.prediccion || 'N/A'}
                        </div>
                      </td>
                      <td style={{
                        padding: '1.25rem 0.875rem',
                        borderBottom: '1px solid #e2e8f0',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.375rem',
                          padding: '0.375rem 0.75rem',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          borderRadius: '0.5rem',
                          color: 'white',
                          fontSize: '0.75rem',
                          fontWeight: '700',
                          boxShadow: '0 2px 6px rgba(16, 185, 129, 0.2)'
                        }}>
                          <CheckCircle size={12} strokeWidth={2.5} />
                          {prediccion.valor_real || 'N/A'}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Table Footer Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
            gap: '1.5rem',
            marginTop: '2rem'
          }}>
            <div style={{
              textAlign: 'center',
              padding: '1.25rem',
              background: '#ecfdf5',
              borderRadius: '0.875rem',
              border: '2px solid #a7f3d0'
            }}>
              <div style={{
                fontSize: '2rem',
                fontWeight: '800',
                color: '#059669',
                marginBottom: '0.375rem'
              }}>
                {prediccionesVerificadas.length > 0 ? '100%' : '0%'}
              </div>
              <div style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#065f46'
              }}>
                Predicciones Acertadas
              </div>
            </div>
            
            <div style={{
              textAlign: 'center',
              padding: '1.25rem',
              background: '#eef2ff',
              borderRadius: '0.875rem',
              border: '2px solid #c7d2fe'
            }}>
              <div style={{
                fontSize: '2rem',
                fontWeight: '800',
                color: '#4338ca',
                marginBottom: '0.375rem'
              }}>
                ±2.8%
              </div>
              <div style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#3730a3'
              }}>
                Margen Error Promedio
              </div>
            </div>
            
            <div style={{
              textAlign: 'center',
              padding: '1.25rem',
              background: '#fffbeb',
              borderRadius: '0.875rem',
              border: '2px solid #fde68a'
            }}>
              <div style={{
                fontSize: '2rem',
                fontWeight: '800',
                color: '#d97706',
                marginBottom: '0.375rem'
              }}>
                1-2h
              </div>
              <div style={{
                fontSize: '0.75rem',
                fontWeight: '600',
                color: '#92400e'
              }}>
                Tiempo Entrega
              </div>
            </div>
          </div>
        </div>

        {/* Pricing Section */}
        <div style={{
          background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 50%, #ecfdf5 100%)',
          border: '3px solid #f59e0b',
          borderRadius: '1.5rem',
          padding: 'clamp(2rem, 5vw, 3rem)',
          marginBottom: '4rem',
          boxShadow: '0 10px 35px rgba(245, 158, 11, 0.2)',
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden'
        }}>
          <div style={{
            position: 'absolute',
            top: '-30%',
            right: '-5%',
            width: '300px',
            height: '300px',
            background: 'radial-gradient(circle, rgba(245, 158, 11, 0.1), transparent 70%)',
            borderRadius: '50%',
            pointerEvents: 'none'
          }} />
          
          <div style={{
            display: 'inline-block',
            padding: '0.5rem 1.25rem',
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            borderRadius: '1.5rem',
            marginBottom: '1.25rem',
            boxShadow: '0 4px 15px rgba(245, 158, 11, 0.25)'
          }}>
            <span style={{
              fontSize: '0.8rem',
              fontWeight: '800',
              color: 'white',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              💎 Inversión Premium
            </span>
          </div>

          <h2 style={{
            fontSize: 'clamp(2rem, 5vw, 3.5rem)',
            fontWeight: '900',
            marginBottom: '1rem',
            lineHeight: '1.1',
            letterSpacing: '-0.025em',
            color: '#0f172a',
            position: 'relative'
          }}>
            <span style={{
              fontSize: 'clamp(1rem, 2.5vw, 1.5rem)',
              fontWeight: '700',
              color: '#64748b',
              display: 'block',
              marginBottom: '0.5rem'
            }}>
              Inversión:
            </span>
            <span style={{
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}>
              S/ 100
            </span>
          </h2>

          <p style={{
            fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
            color: '#64748b',
            marginBottom: '1.75rem',
            fontWeight: '500'
          }}>
            Pago único • Predicción completa con garantía
          </p>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
            gap: '1rem',
            marginBottom: '1.75rem',
            maxWidth: '450px',
            margin: '0 auto 1.75rem'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              padding: '0.625rem',
              background: 'white',
              borderRadius: '0.625rem',
              border: '2px solid #e2e8f0',
              boxShadow: '0 3px 10px rgba(15, 23, 42, 0.06)'
            }}>
              <CheckCircle size={16} style={{color: '#10b981'}} strokeWidth={2.5} />
              <span style={{fontSize: '0.8rem', fontWeight: '600', color: '#0f172a'}}>
                1-2h
              </span>
            </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              padding: '0.625rem',
              background: 'white',
              borderRadius: '0.625rem',
              border: '2px solid #e2e8f0',
              boxShadow: '0 3px 10px rgba(15, 23, 42, 0.06)'
            }}>
              <Shield size={16} style={{color: '#6366f1'}} strokeWidth={2.5} />
              <span style={{fontSize: '0.8rem', fontWeight: '600', color: '#0f172a'}}>
                70% Garantía
              </span>
            </div>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.375rem',
              padding: '0.625rem',
              background: 'white',
              borderRadius: '0.625rem',
              border: '2px solid #e2e8f0',
              boxShadow: '0 3px 10px rgba(15, 23, 42, 0.06)'
            }}>
              <DollarSign size={16} style={{color: '#f59e0b'}} strokeWidth={2.5} />
              <span style={{fontSize: '0.8rem', fontWeight: '600', color: '#0f172a'}}>
                Pago Único
              </span>
            </div>
          </div>

          <button 
            onClick={handleContact}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white',
              fontWeight: '800',
              fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
              padding: 'clamp(1rem, 2.5vw, 1.25rem) clamp(1.5rem, 4vw, 2.25rem)',
              border: 'none',
              borderRadius: '0.75rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 10px 30px rgba(16, 185, 129, 0.35)',
              marginBottom: '1.25rem',
              width: '100%',
              maxWidth: '400px'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 12px 35px rgba(16, 185, 129, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 10px 30px rgba(16, 185, 129, 0.35)';
            }}
          >
            <Phone size={20} strokeWidth={2.5} />
            Solicitar por WhatsApp
            <ArrowRight size={20} strokeWidth={2.5} />
          </button>

          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.625rem',
            padding: '0.875rem 1.25rem',
            background: 'white',
            borderRadius: '0.75rem',
            border: '2px solid #e2e8f0',
            boxShadow: '0 4px 15px rgba(15, 23, 42, 0.08)',
            maxWidth: '320px',
            margin: '0 auto'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 3px 10px rgba(16, 185, 129, 0.25)'
            }}>
              <Phone size={18} color="white" strokeWidth={2.5} />
            </div>
            <div style={{textAlign: 'left'}}>
              <div style={{
                fontSize: '0.7rem',
                color: '#64748b',
                fontWeight: '600',
                marginBottom: '0.125rem'
              }}>
                WhatsApp Directo
              </div>
              <div style={{
                fontSize: '1.125rem',
                fontWeight: '800',
                color: '#0f172a',
                letterSpacing: '0.025em'
              }}>
                942 128 139
              </div>
            </div>
          </div>
        </div>

      
        {/* Process Section */}
        <div style={{ marginBottom: '4rem' }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '3rem'
          }}>
            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: '800',
              color: '#0f172a',
              marginBottom: '0.75rem',
              letterSpacing: '-0.02em'
            }}>
              Proceso Simple en{' '}
              <span style={{
                background: 'linear-gradient(135deg, #6366f1 0%, #10b981 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                4 Pasos
              </span>
            </h2>
            <p style={{
              fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
              color: '#64748b',
              maxWidth: '600px',
              margin: '0 auto'
            }}>
              De tu consulta a tu predicción en menos de 24 horas
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: '1.5rem'
          }}>
            {processSteps.map((step, index) => (
              <div key={index} style={{
                textAlign: 'center'
              }}>
                <div style={{
                  width: '70px',
                  height: '70px',
                  borderRadius: '50%',
                  background: step.gradient,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1rem',
                  boxShadow: `0 8px 25px ${step.color}25`,
                  transition: 'all 0.3s ease',
                  cursor: 'pointer',
                  border: '3px solid white',
                  color: 'white'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'scale(1.05)';
                  e.currentTarget.style.boxShadow = `0 10px 30px ${step.color}30`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = `0 8px 25px ${step.color}25`;
                }}>
                  {step.icon}
                </div>
                
                <div style={{
                  display: 'inline-block',
                  padding: '0.375rem 0.875rem',
                  background: step.gradient,
                  borderRadius: '0.5rem',
                  marginBottom: '0.875rem',
                  fontWeight: '700',
                  fontSize: '0.7rem',
                  color: 'white',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  boxShadow: `0 3px 10px ${step.color}20`
                }}>
                  {step.time}
                </div>
                
                <h3 style={{
                  fontSize: '1.125rem',
                  fontWeight: '700',
                  color: '#0f172a',
                  marginBottom: '0.625rem'
                }}>
                  {step.title}
                </h3>
                
                <p style={{
                  color: '#64748b',
                  fontSize: '0.875rem',
                  lineHeight: '1.5'
                }}>
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div style={{
          background: 'white',
          border: '2px solid #fef3c7',
          borderRadius: '1.5rem',
          padding: 'clamp(2rem, 5vw, 3rem)',
          marginBottom: '4rem',
          boxShadow: '0 6px 25px rgba(245, 158, 11, 0.1)'
        }}>
          <div style={{
            textAlign: 'center',
            marginBottom: '2.5rem'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '0.25rem',
              marginBottom: '1rem'
            }}>
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={24} fill="#f59e0b" color="#f59e0b" strokeWidth={0} />
              ))}
            </div>
            
            <h2 style={{
              fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
              fontWeight: '800',
              color: '#0f172a',
              marginBottom: '0.75rem',
              letterSpacing: '-0.02em'
            }}>
              Clientes{' '}
              <span style={{
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                Satisfechos
              </span>
            </h2>
            
            <p style={{
              fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
              color: '#64748b'
            }}>
              Más de 200 empresas confían en nuestra tecnología
            </p>
          </div>

          <div style={{
            maxWidth: '750px',
            margin: '0 auto'
          }}>
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                style={{
                  display: currentTestimonial === index ? 'block' : 'none'
                }}
              >
                <div style={{
                  background: '#fffbeb',
                  borderRadius: '1rem',
                  padding: 'clamp(1.5rem, 4vw, 2rem)',
                  border: '2px solid #fef3c7'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginBottom: '1rem'
                  }}>
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={16} fill="#f59e0b" color="#f59e0b" strokeWidth={0} />
                    ))}
                  </div>
                  
                  <p style={{
                    fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
                    color: '#0f172a',
                    lineHeight: '1.6',
                    marginBottom: '1.5rem',
                    fontStyle: 'italic',
                    textAlign: 'center',
                    fontWeight: '400'
                  }}>
                    "{testimonial.text}"
                  </p>
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.875rem'
                  }}>
                    <div style={{
                      width: '50px',
                      height: '50px',
                      borderRadius: '50%',
                      background: `linear-gradient(135deg, ${testimonial.color}, ${testimonial.color}dd)`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '1.25rem',
                      fontWeight: '800',
                      boxShadow: `0 4px 15px ${testimonial.color}25`,
                      border: '3px solid white'
                    }}>
                      {testimonial.avatar}
                    </div>
                    <div style={{textAlign: 'left'}}>
                      <div style={{
                        fontSize: '1rem',
                        fontWeight: '700',
                        color: '#0f172a',
                        marginBottom: '0.25rem'
                      }}>
                        {testimonial.author}
                      </div>
                      <div style={{
                        fontSize: '0.8rem',
                        color: '#64748b',
                        marginBottom: '0.375rem',
                        fontWeight: '500'
                      }}>
                        {testimonial.role} • {testimonial.company}
                      </div>
                      <div style={{
                        display: 'inline-block',
                        padding: '0.25rem 0.625rem',
                        background: `${testimonial.color}15`,
                        borderRadius: '0.375rem',
                        fontSize: '0.7rem',
                        fontWeight: '600',
                        color: testimonial.color,
                        border: `1px solid ${testimonial.color}25`
                      }}>
                        ✓ {testimonial.project}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}

            <div style={{
              display: 'flex',
              justifyContent: 'center',
              gap: '0.5rem',
              marginTop: '1.5rem'
            }}>
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentTestimonial(index)}
                  style={{
                    width: currentTestimonial === index ? '2rem' : '0.5rem',
                    height: '0.5rem',
                    borderRadius: '0.25rem',
                    border: 'none',
                    background: currentTestimonial === index 
                      ? 'linear-gradient(90deg, #f59e0b, #d97706)' 
                      : '#cbd5e1',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    boxShadow: currentTestimonial === index ? '0 2px 6px rgba(245, 158, 11, 0.25)' : 'none'
                  }}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Final CTA */}
        <div style={{
          background: 'linear-gradient(135deg, #6366f1 0%, #10b981 50%, #f59e0b 100%)',
          borderRadius: '1.5rem',
          padding: 'clamp(2.5rem, 6vw, 4rem)',
          textAlign: 'center',
          boxShadow: '0 15px 50px rgba(99, 102, 241, 0.3)',
          marginBottom: '3rem',
          border: '4px solid white'
        }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(10px)',
            borderRadius: '1rem',
            marginBottom: '1rem',
            border: '2px solid rgba(255,255,255,0.25)'
          }}>
            <Rocket size={16} color="white" strokeWidth={2.5} />
            <span style={{
              fontSize: '0.75rem',
              fontWeight: '700',
              color: 'white',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Oferta Premium • Actúa Ahora
            </span>
          </div>
          
          <h2 style={{
            fontSize: 'clamp(1.75rem, 4.5vw, 3rem)',
            fontWeight: '800',
            color: 'white',
            marginBottom: '1rem',
            lineHeight: '1.15',
            letterSpacing: '-0.02em'
          }}>
            ¿Listo Para Ganar Tu
            <br />
            Próxima Licitación?
          </h2>
          
          <p style={{
            fontSize: 'clamp(0.875rem, 2.5vw, 1.125rem)',
            color: 'rgba(255,255,255,0.95)',
            marginBottom: '1.75rem',
            maxWidth: '600px',
            margin: '0 auto 1.75rem',
            lineHeight: '1.5',
            fontWeight: '400'
          }}>
            Más de <strong>200 empresas</strong> ya toman decisiones inteligentes
            <br />¡Es tu turno de ganar con IA!
          </p>
          
          <button 
            onClick={handleContact}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.75rem',
              background: 'white',
              color: '#6366f1',
              fontWeight: '800',
              fontSize: 'clamp(1rem, 2.5vw, 1.125rem)',
              padding: 'clamp(1rem, 2.5vw, 1.25rem) clamp(1.5rem, 4vw, 2.25rem)',
              border: 'none',
              borderRadius: '0.75rem',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
              textTransform: 'uppercase',
              letterSpacing: '0.02em',
              marginBottom: '1.5rem'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.12)';
            }}
          >
            <Phone size={20} strokeWidth={2.5} />
            <span>¡Quiero Mi Predicción!</span>
            <ArrowRight size={20} strokeWidth={2.5} />
          </button>

          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 'clamp(1rem, 3vw, 1.5rem)',
            flexWrap: 'wrap',
            fontSize: 'clamp(0.8rem, 2vw, 0.875rem)',
            color: 'white',
            fontWeight: '600'
          }}>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.375rem'}}>
              <CheckCircle size={16} strokeWidth={2.5} />
              <span>Solo S/ 200</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.375rem'}}>
              <Shield size={16} strokeWidth={2.5} />
              <span>Garantía 60%</span>
            </div>
            <div style={{display: 'flex', alignItems: 'center', gap: '0.375rem'}}>
              <Clock size={16} strokeWidth={2.5} />
              <span>Entrega 1-2h</span>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          padding: '2rem 0 1rem',
          borderTop: '2px solid #e2e8f0'
        }}>
          <p style={{
            fontSize: '0.75rem',
            color: '#94a3b8',
            lineHeight: '1.5',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            * La garantía aplica cuando el valor de adjudicación publicado en SEACE está fuera del rango predicho. 
            Términos y condiciones disponibles bajo solicitud.
          </p>
        </div>
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: .5;
          }
        }
        
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .fade-in {
          animation: fadeIn 0.6s ease-out;
        }

        @keyframes slideIn {
          from {
            transform: translateX(-20px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }

        .slide-in {
          animation: slideIn 0.5s ease-out;
        }

        /* Smooth progress bar animation */
        @keyframes progressFill {
          from {
            width: 0%;
          }
        }

        .progress-bar {
          animation: progressFill 1s ease-out;
        }

        /* Hover effects for cards */
        .hover-lift {
          transition: all 0.3s ease;
        }

        .hover-lift:hover {
          transform: translateY(-4px);
          box-shadow: 0 15px 40px rgba(0, 0, 0, 0.1);
        }

        /* Loading dots animation for status indicators */
        @keyframes loadingDots {
          0%, 20% {
            opacity: 0;
          }
          50% {
            opacity: 1;
          }
          80%, 100% {
            opacity: 0;
          }
        }

        .loading-dot {
          animation: loadingDots 1.4s infinite ease-in-out;
        }

        .loading-dot:nth-child(1) {
          animation-delay: -0.32s;
        }

        .loading-dot:nth-child(2) {
          animation-delay: -0.16s;
        }

        /* Gradient background animation */
        @keyframes gradientShift {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }

        .animated-gradient {
          background-size: 200% 200%;
          animation: gradientShift 6s ease infinite;
        }
      `}</style>
    </div>
  );
};

export default PrediccionLicitaciones;